<template>
  <van-tabbar v-model="active" active-color="#8909C3" class="custom-tabbar" @change="handleTabChange">
    <van-tabbar-item v-if="false" icon="flower-o" class="custom-tabbar-item"
      >春信</van-tabbar-item
    >
    <van-tabbar-item class="custom-tabbar-item">
      <template #icon="props">
        <img :src="props.active ? recomSelected : recomIcon" class="tab-icon" />
      </template>
      推荐
    </van-tabbar-item>
    <van-tabbar-item v-if="socialEnabled" class="custom-tabbar-item">
      <template #icon="props">
        <img :src="props.active ? feedSelected : feedIcon" class="tab-icon" />
      </template>
      动态
    </van-tabbar-item>
    <van-tabbar-item v-if="false" icon="home-o" class="custom-tabbar-item"
      >春信助理</van-tabbar-item
    >
    <van-tabbar-item v-if="false" icon="hot-o" class="custom-tabbar-item"
      >套餐</van-tabbar-item
    >
    <van-tabbar-item v-if="false" icon="medal" class="custom-tabbar-item"
      >助理</van-tabbar-item
    >
    <van-tabbar-item :badge="unreadCount>0?unreadCount:''" class="custom-tabbar-item">
      <template #icon="props">
        <img :src="props.active ? messageSelected : messageIcon" class="tab-icon" />
      </template>
      消息
    </van-tabbar-item>
    <van-tabbar-item class="custom-tabbar-item">
      <template #icon="props">
        <img :src="props.active ? mySelected : myIcon" class="tab-icon" />
      </template>
      我的
    </van-tabbar-item>
  </van-tabbar>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthManager } from "@/state/user";
import { countUnRead } from "@/interfaces/message";
import { socialOnState } from "@/interfaces/appVersion";
import recomIcon from "@/resources/images/tarbar/recom.png";
import feedIcon from "@/resources/images/tarbar/feed.png";
import messageIcon from "@/resources/images/tarbar/message.png";
import myIcon from "@/resources/images/tarbar/my.png";
import recomSelected from "@/resources/images/tarbar/recom-selected.png";
import feedSelected from "@/resources/images/tarbar/feed-selected.png";
import messageSelected from "@/resources/images/tarbar/message-selected.png";
import mySelected from "@/resources/images/tarbar/my-selected.png";

// 社交功能是否启用（响应式，版本检测返回后自动更新）
const socialEnabled = socialOnState;

const router = useRouter();
const route = useRoute();
const authManager = useAuthManager();

// Tab 索引到路由的映射（社交功能开启时）
const tabRoutesWithSocial = ['/', '/video', '/messages/center', '/profile'];

// Tab 索引到路由的映射（社交功能关闭时）
const tabRoutesWithoutSocial = ['/', '/messages/center', '/profile'];

// 根据社交功能状态获取对应的路由映射
const tabRoutes = computed(() => socialEnabled.value ? tabRoutesWithSocial : tabRoutesWithoutSocial);

// 根据当前路由获取应该高亮的 tab 索引
const getCurrentTabIndex = () => {
  const path = route.path;
  // 处理特殊路由映射
  if (path === '/' || path === '/recommended' || path.startsWith('/top?') || path.startsWith('/?')) {
    return 0; // 推荐页
  }
  // 如果社交功能关闭且当前是视频页面，跳转到推荐页
  if (!socialEnabled.value && (path === '/video' || path.startsWith('/video?'))) {
    return 0; // 跳转到推荐页
  }
  const index = tabRoutes.value.findIndex(routePath => path === routePath || path.startsWith(routePath + '?'));
  return index >= 0 ? index : 0; // 默认选中推荐页
};

const active = ref(getCurrentTabIndex()); // 根据当前路由高亮对应菜单项

const unreadCount = ref(0); // 消息未读数量

// 轮询配置
const POLLING_INTERVAL = 5000; // 5秒轮询
let pollingTimer = null;
let isPollingStopped = false;

// 获取未读消息数
const fetchUnreadCount = async () => {
  if (isPollingStopped || !authManager.authToken || !pollingTimer) {
    return;
  }
  try {
    // 调用接口获取未读数量
    const count = await countUnRead();
    unreadCount.value = count;
  } catch (error) {
    console.error('获取消息未读数量失败：', error);
    unreadCount.value = 0; // 异常时置0，避免角标显示错误
  }
};

// 开始轮询
const startPolling = () => {
  if (pollingTimer || document.hidden || !authManager.authToken) return;
  isPollingStopped = false;
  fetchUnreadCount(); // 立即执行一次
  pollingTimer = setInterval(fetchUnreadCount, POLLING_INTERVAL);
};

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
    isPollingStopped = true;
  }
};

// 页面可见性变化处理
const handleVisibilityChange = () => {
  if (document.hidden) {
    stopPolling();
  } else if (authManager.authToken) {
    startPolling();
  }
};

// 组件挂载
onMounted(() => {
  if (authManager.authToken) {
    startPolling();
  }
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

// 组件卸载
onUnmounted(() => {
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

// 处理 tab 点击
const handleTabChange = (index) => {
  const targetRoute = tabRoutes.value[index];
  if (targetRoute) {
    router.push(targetRoute);
  }
};

// 监听路由变化/社交开关变化，确保tab高亮正确
watch(
  [() => route.path, socialEnabled],
  () => {
    active.value = getCurrentTabIndex();
    // 每次路由变化时刷新未读数量
    fetchUnreadCount();
  }
);

// 监听用户登录状态变化，停止
watch(
  () => authManager.authToken,
  () => {
    // 直接从 localStorage 检查 token，如果为空就不轮询
    const hasToken = !!localStorage.getItem('token');
    if (!hasToken) {
      stopPolling();
    } else if (!document.hidden) {
      startPolling();
    }
  }
);
</script>

<style scoped>
.custom-tabbar {
  height: 70px;
  padding-bottom: calc(-10px + var(--safe-area-inset-bottom, 0px));
  box-sizing: content-box;
}

.custom-tabbar-item {
  flex-direction: column;
  justify-content: center;
  padding-top: 6px;
}

/* tabbar 图片图标：选中/未选中双图切换 */
.tab-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
  display: block;
}

:deep(.van-tabbar-item__text) {
  font-size: 14px;
  margin-top: 2px;
}
</style>
